import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { getReportMeta, queryReport, exportReport, saveReportFilter, deleteSavedFilter, getVendorSummary, getDownloadHistory, deleteDownloadLog } from '../services/api';
import { FileSpreadsheet, FileText, Search, Filter, Save, RotateCcw, ChevronLeft, ChevronRight, ChevronDown, BarChart3, X, Columns3, SlidersHorizontal, History, Trash2, Mail } from 'lucide-react';
import { SchedulesManager } from '../components/reports/SchedulesManager';
import { toast } from 'sonner';

const INITIAL_FILTERS = {
  vendors: [], branches: [], statuses: [], bankNames: [],
  search: '', dateFrom: '', dateTo: '',
  freezeDateFrom: '', freezeDateTo: '',
  freezeAmountMin: null, freezeAmountMax: null,
  assignedOnly: null, columns: [],
  sortField: 'companyName', sortDir: 'asc', page: 1, pageSize: 50
};

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
    {label}<button onClick={onRemove} className="hover:bg-blue-200 rounded-full p-0.5"><X className="w-3 h-3" /></button>
  </span>
);

const FilterDropdown = ({ label, options, selected, onChange, placeholder }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-600">{label}</Label>
    <Select onValueChange={(val) => { if (val && !selected.includes(val)) onChange([...selected, val]); }}>
      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={placeholder || `Select ${label}...`} /></SelectTrigger>
      <SelectContent>
        {options.filter(o => !selected.includes(o)).map(opt => (
          <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    {selected.length > 0 && (
      <div className="flex flex-wrap gap-1 mt-1">
        {selected.map(v => <FilterChip key={v} label={v} onRemove={() => onChange(selected.filter(x => x !== v))} />)}
      </div>
    )}
  </div>
);

const ReportsPage = () => {
  const [tab, setTab] = useState('custom');
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [vendorSummary, setVendorSummary] = useState(null);
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [showColumns, setShowColumns] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [downloadHistory, setDownloadHistory] = useState(null);

  useEffect(() => {
    getReportMeta().then(r => setMeta(r.data)).catch(err => console.error('Meta load failed:', err.message));
  }, []);

  const handleQuery = async (pageOverride) => {
    setLoading(true);
    try {
      const f = { ...filters, page: pageOverride || filters.page };
      if (!f.columns.length && meta) f.columns = meta.columns.slice(0, 12);
      const res = await queryReport(f);
      setResult(res.data);
    } catch (err) { console.error('Query failed:', err.message); toast.error('Report query failed'); }
    finally { setLoading(false); }
  };

  const handleExport = async (format) => {
    try {
      const f = { ...filters, page: 1, pageSize: 99999 };
      if (!f.columns.length && meta) f.columns = meta.columns.slice(0, 12);
      const res = await exportReport(format, f);
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `Report.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch { toast.error('Export failed'); }
  };

  const handleSave = async () => {
    if (!saveName.trim()) return;
    try {
      await saveReportFilter(saveName, filters);
      toast.success('Filter saved');
      setShowSaveDialog(false); setSaveName('');
      const r = await getReportMeta(); setMeta(r.data);
    } catch { toast.error('Save failed'); }
  };

  const handleLoadPreset = (preset) => {
    setFilters({ ...INITIAL_FILTERS, ...preset.filters });
    toast.success(`Loaded: ${preset.name}`);
  };

  const handleDeletePreset = async (id) => {
    try { await deleteSavedFilter(id); const r = await getReportMeta(); setMeta(r.data); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const loadVendorSummary = async () => {
    try { const res = await getVendorSummary(); setVendorSummary(res.data); } catch (err) { console.error('Summary failed:', err.message); }
  };

  useEffect(() => { if (tab === 'summary') loadVendorSummary(); }, [tab]);

  const loadHistory = async () => {
    try { const res = await getDownloadHistory(100, 0); setDownloadHistory(res.data); } catch (err) { console.error('History load failed:', err.message); }
  };
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab]);

  const handleDeleteLog = async (id) => {
    try { await deleteDownloadLog(id); loadHistory(); toast.success('Log deleted'); } catch { toast.error('Failed to delete'); }
  };

  const runWithFilters = async (newFilters) => {
    setTab('custom');
    setFilters(newFilters);
    setLoading(true);
    try {
      const f = { ...newFilters };
      if (!f.columns.length && meta) f.columns = meta.columns.slice(0, 12);
      const res = await queryReport(f);
      setResult(res.data);
    } catch (err) { console.error('Query failed:', err.message); }
    finally { setLoading(false); }
  };

  const handleDrillDown = (vendorName, status) => {
    const f = { ...INITIAL_FILTERS, page: 1 };
    if (vendorName) f.vendors = [vendorName];
    if (status) f.statuses = [status];
    runWithFilters(f);
  };

  const handleBranchDrillDown = (vendorName, branchName) => {
    runWithFilters({ ...INITIAL_FILTERS, vendors: [vendorName], branches: [branchName], page: 1 });
  };

  /** One-click Excel/PDF export of a specific drill-down slice without
   *  forcing the user to switch tabs and click again.
   */
  const handleQuickExport = async (slice, format = 'excel') => {
    const f = { ...INITIAL_FILTERS, ...slice, page: 1, pageSize: 99999 };
    if (!f.columns.length && meta) f.columns = meta.columns.slice(0, 12);
    try {
      const res = await exportReport(format, f);
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const tag = [slice.vendors?.[0], slice.branches?.[0], slice.statuses?.[0]].filter(Boolean).join('_').replace(/\s+/g, '-') || 'Drilldown';
      a.download = `Report_${tag}.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch { toast.error('Export failed'); }
  };

  const uf = (key, val) => setFilters(prev => ({ ...prev, [key]: val, page: 1 }));
  const activeFilterCount = [filters.vendors.length, filters.branches.length, filters.statuses.length, filters.bankNames.length, filters.search ? 1 : 0, filters.dateFrom ? 1 : 0, filters.dateTo ? 1 : 0, filters.freezeDateFrom ? 1 : 0, filters.freezeAmountMin !== null ? 1 : 0, filters.assignedOnly !== null ? 1 : 0].reduce((a, b) => a + b, 0);

  if (!meta) return (<><Navbar /><div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">Loading...</div></>);

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" data-testid="reports-page-title">Reports</h1>
            <p className="text-slate-600 mt-1">Custom reports, vendor summaries & analytics</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="custom" className="gap-1.5"><Filter className="w-3.5 h-3.5" />Custom Report</TabsTrigger>
            <TabsTrigger value="summary" className="gap-1.5"><BarChart3 className="w-3.5 h-3.5" />Vendor Summary</TabsTrigger>
            <TabsTrigger value="history" className="gap-1.5"><History className="w-3.5 h-3.5" />Download History</TabsTrigger>
            <TabsTrigger value="schedules" className="gap-1.5" data-testid="reports-tab-schedules"><Mail className="w-3.5 h-3.5" />Schedules</TabsTrigger>
          </TabsList>

          <TabsContent value="custom" className="space-y-4 mt-4">
            {/* Saved Presets */}
            {meta.savedFilters?.length > 0 && (
              <div className="flex gap-2 flex-wrap items-center">
                <span className="text-xs text-slate-500 font-semibold">Saved:</span>
                {meta.savedFilters.map(f => (
                  <div key={f.id} className="inline-flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleLoadPreset(f)}>{f.name}</Button>
                    <button onClick={() => handleDeletePreset(f.id)} className="text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Main Filter Bar */}
            <Card>
              <CardContent className="p-4 space-y-3">
                {/* Row 1: Primary filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <FilterDropdown label="Vendor" options={meta.vendors} selected={filters.vendors} onChange={(v) => uf('vendors', v)} />
                  <FilterDropdown label="Branch" options={meta.branches} selected={filters.branches} onChange={(v) => uf('branches', v)} />
                  <FilterDropdown label="Status" options={meta.statuses} selected={filters.statuses} onChange={(v) => uf('statuses', v)} />
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Search</Label>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <Input placeholder="Any keyword..." value={filters.search} onChange={(e) => uf('search', e.target.value)} className="h-8 text-xs pl-7" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Assignment</Label>
                    <Select value={filters.assignedOnly === null ? 'all' : filters.assignedOnly ? 'yes' : 'no'} onValueChange={(v) => uf('assignedOnly', v === 'all' ? null : v === 'yes')}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Banks</SelectItem>
                        <SelectItem value="yes">Assigned Only</SelectItem>
                        <SelectItem value="no">Unassigned Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Advanced filters toggle */}
                <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
                  <CollapsibleTrigger asChild>
                    <button className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                      <SlidersHorizontal className="w-3.5 h-3.5" />Advanced Filters
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-2">
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 p-3 bg-slate-50 rounded-lg border">
                      <FilterDropdown label="Bank Name" options={meta.bankNames} selected={filters.bankNames} onChange={(v) => uf('bankNames', v)} />
                      <div className="space-y-1"><Label className="text-xs text-slate-600">Created From</Label><Input type="date" value={filters.dateFrom} onChange={(e) => uf('dateFrom', e.target.value)} className="h-8 text-xs" /></div>
                      <div className="space-y-1"><Label className="text-xs text-slate-600">Created To</Label><Input type="date" value={filters.dateTo} onChange={(e) => uf('dateTo', e.target.value)} className="h-8 text-xs" /></div>
                      <div className="space-y-1"><Label className="text-xs text-slate-600">Freeze Date From</Label><Input type="date" value={filters.freezeDateFrom} onChange={(e) => uf('freezeDateFrom', e.target.value)} className="h-8 text-xs" /></div>
                      <div className="space-y-1"><Label className="text-xs text-slate-600">Freeze Amt Min</Label><Input type="number" value={filters.freezeAmountMin || ''} onChange={(e) => uf('freezeAmountMin', e.target.value ? parseFloat(e.target.value) : null)} className="h-8 text-xs" /></div>
                      <div className="space-y-1"><Label className="text-xs text-slate-600">Freeze Amt Max</Label><Input type="number" value={filters.freezeAmountMax || ''} onChange={(e) => uf('freezeAmountMax', e.target.value ? parseFloat(e.target.value) : null)} className="h-8 text-xs" /></div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Column selector toggle */}
                <Collapsible open={showColumns} onOpenChange={setShowColumns}>
                  <CollapsibleTrigger asChild>
                    <button className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                      <Columns3 className="w-3.5 h-3.5" />Columns ({(filters.columns.length || 12)} selected)
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showColumns ? 'rotate-180' : ''}`} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-2">
                    <div className="flex flex-wrap gap-1 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                      {meta.columns.map(c => {
                        const selected = filters.columns.length ? filters.columns.includes(c) : meta.columns.slice(0, 12).includes(c);
                        return (
                          <Badge key={c} variant={selected ? "default" : "outline"}
                            className={`cursor-pointer text-xs ${selected ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-white hover:bg-indigo-100 text-slate-600'}`}
                            onClick={() => {
                              const current = filters.columns.length ? [...filters.columns] : [...meta.columns.slice(0, 12)];
                              if (current.includes(c)) uf('columns', current.filter(x => x !== c));
                              else uf('columns', [...current, c]);
                            }}>{meta.columnLabels[c]}</Badge>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Actions */}
                <div className="flex items-center justify-between pt-1 border-t">
                  <div className="flex gap-2">
                    <Button onClick={() => handleQuery(1)} size="sm" className="gap-1.5" data-testid="apply-filters-btn">
                      <Search className="w-3.5 h-3.5" />{loading ? 'Loading...' : 'Run Report'}{activeFilterCount > 0 && <Badge className="bg-white/20 text-xs ml-1">{activeFilterCount}</Badge>}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExport('excel')} className="gap-1" data-testid="export-report-excel"><FileSpreadsheet className="w-3.5 h-3.5" />Excel</Button>
                    <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} className="gap-1" data-testid="export-report-pdf"><FileText className="w-3.5 h-3.5" />PDF</Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowSaveDialog(true)} className="gap-1 text-xs"><Save className="w-3.5 h-3.5" />Save</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setFilters(INITIAL_FILTERS); setResult(null); }} className="gap-1 text-xs"><RotateCcw className="w-3.5 h-3.5" />Reset</Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            {result && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-sm">{result.total} records found</CardTitle>
                    <div className="flex gap-2 text-xs flex-wrap">
                      <Badge className="bg-green-100 text-green-800 border-green-200">Active {result.summary.active}</Badge>
                      <Badge className="bg-red-100 text-red-800 border-red-200">Freezed {result.summary.freezed}</Badge>
                      <Badge className="bg-teal-100 text-teal-800 border-teal-200">Unassigned {result.summary.unassigned}</Badge>
                      <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200">Assigned {result.summary.assigned}</Badge>
                      {result.summary.totalFreezedAmount > 0 && <Badge className="bg-red-50 text-red-700 border-red-200">Freezed: ₹{Math.round(result.summary.totalFreezedAmount).toLocaleString('en-IN')}</Badge>}
                    </div>
                  </div>
                  {result.summary.vendorCounts && Object.keys(result.summary.vendorCounts).length > 1 && (
                    <div className="flex gap-1.5 flex-wrap mt-1">
                      <span className="text-[10px] text-slate-500 font-semibold">By Vendor:</span>
                      {Object.entries(result.summary.vendorCounts).map(([vendor, count]) => (
                        <Badge key={vendor} variant="outline" className="text-[10px] py-0">{vendor}: {count}</Badge>
                      ))}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-y bg-slate-50">
                          {result.columns.map(c => (
                            <th key={c} className="text-left px-3 py-2 text-xs font-semibold text-slate-600 whitespace-nowrap cursor-pointer hover:text-blue-600"
                              onClick={() => { uf('sortField', c); uf('sortDir', filters.sortField === c && filters.sortDir === 'asc' ? 'desc' : 'asc'); setTimeout(() => handleQuery(1), 50); }}>
                              {result.columnLabels[c]}{filters.sortField === c && (filters.sortDir === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.map((row, i) => {
                          const prevVendor = i > 0 ? result.rows[i - 1].vendorName : null;
                          const showVendorHeader = row.vendorName && row.vendorName !== prevVendor;
                          return (
                            <React.Fragment key={`r-${i}`}>
                              {showVendorHeader && (
                                <tr className="bg-slate-100"><td colSpan={result.columns.length} className="px-3 py-1.5 text-xs font-bold text-slate-700">{row.vendorName} ({result.summary.vendorCounts?.[row.vendorName] || ''})</td></tr>
                              )}
                              <tr className={`border-b hover:bg-slate-50 ${i % 2 === 1 ? 'bg-slate-25' : ''}`}>
                                {result.columns.map(c => (
                                  <td key={`${i}-${c}`} className="px-3 py-1.5 text-xs whitespace-nowrap max-w-[180px] truncate">{row[c] || '-'}</td>
                                ))}
                              </tr>
                            </React.Fragment>
                          );
                        })}
                        {result.rows.length === 0 && <tr><td colSpan={result.columns.length} className="p-8 text-center text-slate-500">No records match</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {result.totalPages > 1 && (
                    <div className="flex items-center justify-between p-3 border-t">
                      <span className="text-xs text-slate-500">Page {result.page} of {result.totalPages}</span>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" className="h-7" disabled={result.page <= 1} onClick={() => { setFilters(f => ({ ...f, page: f.page - 1 })); handleQuery(filters.page - 1); }}><ChevronLeft className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" className="h-7" disabled={result.page >= result.totalPages} onClick={() => { setFilters(f => ({ ...f, page: f.page + 1 })); handleQuery(filters.page + 1); }}><ChevronRight className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="summary" className="space-y-4 mt-4">
            {vendorSummary && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { label: 'Total Banks', val: vendorSummary.grand.total, color: 'blue', click: () => runWithFilters({ ...INITIAL_FILTERS, page: 1 }) },
                    { label: 'Active', val: vendorSummary.grand.active, color: 'green', click: () => runWithFilters({ ...INITIAL_FILTERS, statuses: ['Active'], page: 1 }) },
                    { label: 'Freezed', val: vendorSummary.grand.freezed, color: 'red', click: () => runWithFilters({ ...INITIAL_FILTERS, statuses: ['Freezed - Transaction', 'Freezed - CBI'], page: 1 }) },
                    { label: 'Unassigned (Branch)', val: vendorSummary.grand.unassignedBranch, color: 'amber', click: () => runWithFilters({ ...INITIAL_FILTERS, assignedOnly: false, page: 1 }) },
                    { label: 'Freezed Amount', val: `₹${Math.round(vendorSummary.grand.freezedAmount).toLocaleString('en-IN')}`, color: 'red', isText: true },
                  ].map((kpi, i) => (
                    <Card key={i} className={`${kpi.click ? 'cursor-pointer hover:shadow-md' : ''} transition-shadow`} onClick={kpi.click}>
                      <CardContent className="p-4">
                        <div className={`text-xs text-${kpi.color}-600 font-semibold`}>{kpi.label}</div>
                        <div className={`${kpi.isText ? 'text-xl' : 'text-2xl'} font-bold text-${kpi.color}-700 mt-1`}>{kpi.val}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Vendor-wise Breakdown</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-y bg-slate-50">
                            <th className="text-left p-3 text-xs font-semibold">Vendor</th>
                            <th className="text-center p-3 text-xs font-semibold">Total</th>
                            <th className="text-center p-3 text-xs font-semibold text-green-700">Active</th>
                            <th className="text-center p-3 text-xs font-semibold text-red-700">Freezed</th>
                            <th className="text-center p-3 text-xs font-semibold text-teal-700">Unassigned</th>
                            <th className="text-center p-3 text-xs font-semibold text-blue-700">New</th>
                            <th className="text-center p-3 text-xs font-semibold text-amber-700">In Process</th>
                            <th className="text-center p-3 text-xs font-semibold text-slate-600">Inactive</th>
                            <th className="text-center p-3 text-xs font-semibold text-orange-700">Return</th>
                            <th className="text-center p-3 text-xs font-semibold text-indigo-700">Assigned</th>
                            <th className="text-center p-3 text-xs font-semibold text-red-600">Freezed Amt</th>
                            <th className="text-center p-3 text-xs font-semibold w-12">Export</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vendorSummary.vendors.map(v => (
                            <React.Fragment key={v.vendorId}>
                              <tr className="border-b hover:bg-slate-50">
                                <td className="p-3">
                                  <button onClick={() => setExpandedVendor(expandedVendor === v.vendorId ? null : v.vendorId)} className="flex items-center gap-1 font-medium text-blue-700 hover:underline text-left">
                                    {expandedVendor === v.vendorId ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}{v.vendorName}
                                  </button>
                                </td>
                                <td className="p-3 text-center font-semibold cursor-pointer hover:text-blue-600" onClick={() => handleDrillDown(v.vendorName)}>{v.total}</td>
                                <td className="p-3 text-center text-green-700 cursor-pointer hover:underline" onClick={() => handleDrillDown(v.vendorName, 'Active')}>{v.active || '-'}</td>
                                <td className="p-3 text-center text-red-700 cursor-pointer hover:underline" onClick={() => handleDrillDown(v.vendorName, 'Freezed - Transaction')}>{v.freezed || '-'}</td>
                                <td className="p-3 text-center text-teal-700 cursor-pointer hover:underline" onClick={() => handleDrillDown(v.vendorName, 'Unassigned')}>{v.unassigned || '-'}</td>
                                <td className="p-3 text-center text-blue-700 cursor-pointer hover:underline" onClick={() => handleDrillDown(v.vendorName, 'New Bank')}>{v.newBank || '-'}</td>
                                <td className="p-3 text-center text-amber-700 cursor-pointer hover:underline" onClick={() => handleDrillDown(v.vendorName, 'In Process')}>{v.inProcess || '-'}</td>
                                <td className="p-3 text-center text-slate-600 cursor-pointer hover:underline" onClick={() => handleDrillDown(v.vendorName, 'Inactive')}>{v.inactive || '-'}</td>
                                <td className="p-3 text-center text-orange-700 cursor-pointer hover:underline" onClick={() => handleDrillDown(v.vendorName, 'Return')}>{v.returned || '-'}</td>
                                <td className="p-3 text-center text-indigo-700">{v.assigned || '-'}</td>
                                <td className="p-3 text-center text-red-600 font-mono text-xs">{v.freezedAmount > 0 ? `₹${Math.round(v.freezedAmount).toLocaleString('en-IN')}` : '-'}</td>
                                <td className="p-3 text-center">
                                  <button
                                    onClick={() => handleQuickExport({ vendors: [v.vendorName] }, 'excel')}
                                    title={`Export all ${v.total} banks of ${v.vendorName} (Excel)`}
                                    className="p-1 rounded text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                                    data-testid={`quick-export-vendor-${v.vendorName}`}
                                  >
                                    <FileSpreadsheet className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              </tr>
                              {expandedVendor === v.vendorId && Object.keys(v.branches).length > 0 && (
                                <tr><td colSpan={12} className="p-0">
                                  <div className="bg-indigo-50/50 px-6 py-3">
                                    <div className="text-xs font-semibold text-indigo-700 mb-2">Branches — {v.vendorName}</div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                      {Object.entries(v.branches).sort((a, b) => b[1].total - a[1].total).map(([name, stats]) => (
                                        <div key={name}
                                          className="relative p-2 bg-white rounded-lg border hover:shadow-sm hover:border-indigo-300 transition-all">
                                          <div onClick={() => handleBranchDrillDown(v.vendorName, name)} className="cursor-pointer pr-6" data-testid={`drill-branch-${name}`}>
                                            <div className="text-xs font-semibold text-indigo-700 truncate">{name}</div>
                                            <div className="flex gap-2 mt-1 text-[10px]">
                                              <span>{stats.total} total</span>
                                              {stats.active > 0 && <span className="text-green-600">{stats.active} active</span>}
                                              {stats.freezed > 0 && <span className="text-red-600">{stats.freezed} freezed</span>}
                                            </div>
                                          </div>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleQuickExport({ vendors: [v.vendorName], branches: [name] }, 'excel'); }}
                                            title={`Export ${stats.total} banks (Excel)`}
                                            className="absolute top-1 right-1 p-1 rounded text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                                            data-testid={`quick-export-branch-${name}`}
                                          >
                                            <FileSpreadsheet className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td></tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" />Download History</CardTitle>
                  <span className="text-xs text-slate-500">{downloadHistory?.total || 0} total downloads</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-y bg-slate-50">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600">Date & Time</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600">User</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600">Report Type</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600">Format</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600">Filters Applied</th>
                        <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-600">Records</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {downloadHistory?.logs?.map((log, i) => (
                        <tr key={log.id} className={`border-b hover:bg-slate-50 ${i % 2 === 1 ? 'bg-slate-25' : ''}`}>
                          <td className="px-4 py-2 text-xs text-slate-700 whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            <span className="text-slate-400 ml-1">{new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                          </td>
                          <td className="px-4 py-2 text-xs">
                            <span className="font-medium">{log.userName}</span>
                            <Badge variant="outline" className="ml-1 text-[10px] py-0">{log.userRole}</Badge>
                          </td>
                          <td className="px-4 py-2 text-xs font-medium text-slate-700">{log.reportType}</td>
                          <td className="px-4 py-2">
                            <Badge className={log.format === 'PDF' ? 'bg-red-100 text-red-800 border-red-200 text-[10px]' : 'bg-green-100 text-green-800 border-green-200 text-[10px]'}>
                              {log.format === 'PDF' ? <FileText className="w-3 h-3 mr-1" /> : <FileSpreadsheet className="w-3 h-3 mr-1" />}{log.format}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-500 max-w-[250px] truncate">{log.filters || '-'}</td>
                          <td className="px-4 py-2 text-xs text-center font-mono">{log.recordCount}</td>
                          <td className="px-4 py-2 text-right">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-600" onClick={() => handleDeleteLog(log.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {(!downloadHistory?.logs || downloadHistory.logs.length === 0) && (
                        <tr><td colSpan={7} className="p-8 text-center text-slate-500">No download history yet. Export a report to see it here.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedules" className="space-y-4 mt-4">
            <SchedulesManager savedFilters={meta?.savedFilters || []} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Save Filter Preset</DialogTitle></DialogHeader>
          <div><Label>Preset Name</Label><Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. Frozen Banks Last Week" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button><Button onClick={handleSave}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportsPage;
